import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the free-oil progress bar in step with the cart.
 *
 * The count cannot come from `/cart.js`: which lines qualify is decided by product tags, and the
 * cart JSON carries none. `templates/cart.eq-free-oil.liquid` does the counting in Liquid and this
 * reads the number back from it. The section renders the same count server side, so a reload is
 * already correct and nothing is fetched until the cart changes.
 */
class EqFreeOilBar extends Component {
  #previousFree = 0;
  #successTimeout = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#previousFree = Math.floor(this.#eligible / this.#goal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    clearTimeout(this.#successTimeout);

    super.disconnectedCallback();
  }

  get #goal() {
    return Math.max(Number(this.dataset.goal) || 1, 1);
  }

  /** The count the bar is currently showing, read back off the rendered width. */
  get #eligible() {
    return Number(this.dataset.eligible) || 0;
  }

  /**
   * The event fires when the cart write starts, not when it finishes, and carries a promise that
   * settles once it has. Refreshing without awaiting that reads the cart back before the change
   * has landed and shows the previous count.
   *
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCartUpdate = (event) => {
    const settled = event.promise;

    if (settled && typeof settled.then === 'function') {
      settled.then(() => this.#refresh()).catch(() => {});
      return;
    }

    this.#refresh();
  };

  async #refresh() {
    let eligible;

    try {
      const url = `${window.Theme?.routes?.cart_url ?? '/cart'}?view=eq-free-oil`;
      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const payload = parsed.getElementById('eq-free-oil-items')?.textContent;
      if (!payload) return;

      eligible = Number(JSON.parse(payload).eligible_items) || 0;
    } catch {
      // Offline, or cancelled by a navigation — leave the bar as rendered.
      return;
    }

    this.#render(eligible);
  }

  /** @param {number} eligible */
  #render(eligible) {
    const goal = this.#goal;
    const free = Math.floor(eligible / goal);
    const towardsNext = eligible % goal;

    this.dataset.eligible = String(eligible);
    this.style.setProperty('--eq-free-oil-progress', `${(towardsNext / goal) * 100}%`);

    const { message } = this.refs;
    if (message instanceof HTMLElement) {
      const unlocked = free === 1 ? this.dataset.unlockedOne : this.dataset.unlockedOther;

      const template =
        free > 0
          ? (unlocked ?? '').replace('[free]', String(free))
          : (this.dataset.remainingTemplate ?? '').replace('[remaining]', String(goal - towardsNext));

      message.innerHTML = template.replace('[items]', String(eligible));
    }

    // The success image is a reward for crossing a goal, not a state — it shows only when a new
    // free oil has just been unlocked, then hides itself.
    if (free > this.#previousFree) this.#flashSuccess();
    this.#previousFree = free;
  }

  #flashSuccess() {
    if (!this.refs.success) return;

    clearTimeout(this.#successTimeout);
    this.dataset.unlocked = 'true';

    this.#successTimeout = setTimeout(() => {
      delete this.dataset.unlocked;
    }, 2500);
  }
}

if (!customElements.get('eq-free-oil-bar')) {
  customElements.define('eq-free-oil-bar', EqFreeOilBar);
}
